import { useEffect, useId, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, SparklesIcon, UserIcon } from 'lucide-react';
import { useOverlayActions } from '../../contexts/StoreContext';
import { useSignedIn } from '../../contexts/AccountContext';
import { shopLabels } from '../../data/shopLabels';
import { mainNav } from '../../data/navigation';
import { pageActions } from '../../data/pageActions';
import { Logo } from './Logo';
import { AnnouncementBar } from './AnnouncementBar';
import { CartButton } from './CartButton';
import { MobileMenu } from './MobileMenu';
import { btnGhost, btnPrimary } from '../../ui/styles';

/** Vanaf hier krijgt de header een eigen vlak en een haarlijn. */
const SOLID_AFTER = 8;
/** Boven deze positie blijft de header altijd staan. */
const HIDE_AFTER = 240;
/** Zoveel px in één richting voordat de header verdwijnt of terugkomt: een trillend trackpad doet niets. */
const HIDE_DISTANCE = 48;
const SHOW_DISTANCE = 24;
const iconBtn = btnGhost;

/**
 * Sticky header met een vaste hoogte (72px). Hij krimpt niet meer bij scrollen: dat gaf
 * verspringende inhoud en vroeg om drempel-trucs. Alleen het vlak verandert (sand → shell met
 * haarlijn). Verdwijnt bij naar beneden scrollen en komt direct terug bij omhoog scrollen;
 * blijft staan als het menu open is of de toetsenbordfocus erin staat.
 *
 * Computer: logo · navigatie · huidtest, account, winkelmand en de hoofdknop "Plan een afspraak"
 * (de salon is de helft van het bedrijf en hoort altijd één klik weg).
 * Telefoon: logo · winkelmand · menu. Huidadvies en boeken staan in het menu en de actiebalk.
 */
export function Header() {
  const [open, setOpen] = useState(false);
  const [solid, setSolid] = useState(false);
  const [hidden, setHidden] = useState(false);
  const {
    pathname
  } = useLocation();
  // Alleen stabiele acties: de header tekent niet opnieuw als de winkelmand verandert.
  const {
    openCart,
    openQuiz,
    openBooking
  } = useOverlayActions();
  const signedIn = useSignedIn();
  const menuId = useId();
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const accountLabel = signedIn ? 'Mijn omgeving' : 'Inloggen';
  useEffect(() => {
    setOpen(false);
    setHidden(false);
  }, [pathname]);
  useEffect(() => {
    let last = 0;
    let dir: 1 | -1 = 1;
    /** Waar de huidige scrollrichting begon. */
    let anchor = 0;
    let frame = 0;

    // iOS "stuitert" voorbij boven- en onderkant; die waarden tellen niet mee.
    const position = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      return Math.min(Math.max(window.scrollY, 0), Math.max(max, 0));
    };

    // Hooguit één keer per beeld; React tekent alleen opnieuw als een stand echt omslaat.
    const update = () => {
      frame = 0;
      const y = position();
      setSolid(y > SOLID_AFTER);
      if (y !== last) {
        const nextDir = y > last ? 1 : -1;
        if (nextDir !== dir) {
          dir = nextDir;
          anchor = last;
        }
        last = y;
      }
      if (y <= HIDE_AFTER) setHidden(false);else if (dir === 1 && y - anchor >= HIDE_DISTANCE) setHidden(true);else if (dir === -1 && anchor - y >= SHOW_DISTANCE) setHidden(false);
    };
    last = anchor = position();
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  // Menu open: Escape sluit en zet de focus terug; de pagina eronder scrolt niet mee.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      toggleRef.current?.focus();
    };
    const root = document.documentElement;
    const previous = root.style.overflow;
    root.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      root.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // De hoogte die de header nu inneemt, voor vastgezette delen elders (ui/sections.ts › belowHeader).
  const barRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    setHeight(el.offsetHeight);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setHeight(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    document.documentElement.style.setProperty('--header-offset', `${hidden && !open ? 0 : height}px`);
  }, [hidden, open, height]);
  useEffect(() => () => document.documentElement.style.removeProperty('--header-offset'), []);
  const runAndClose = (action: () => void) => () => {
    setOpen(false);
    action();
  };
  return <div ref={barRef}
  // Focus in de header (Tab vanaf de skiplink) mag nooit achter een verborgen header vallen.
  onFocus={() => setHidden(false)} className={`sticky top-0 z-header transition-transform duration-300 ease-soft motion-reduce:transition-none ${hidden && !open ? '-translate-y-full' : 'translate-y-0'}`} data-id="element-8058" data-mp="18tqlw7hd4hqo">
      <AnnouncementBar data-id="element-8059" data-mp="1rixa2a0xe1jv" />

      <header className={`relative border-b transition-[background-color,border-color] duration-200 ease-soft ${solid || open ? 'border-line bg-shell' : 'border-transparent bg-sand'}`} data-id="element-8060" data-mp="1rjowau8mumxh">
        <div className="mx-auto flex h-[72px] max-w-content items-center justify-between gap-6 px-6 lg:px-10" data-id="element-8061" data-mp="21iedekspg3co">
          <Logo className="h-9 lg:h-10" data-id="element-8062" data-mp="26fo403rtyl23" />

          <nav aria-label="Hoofdnavigatie" className="hidden items-center gap-5 lg:flex xl:gap-7" data-id="element-8063" data-mp="73bsl0elp9g3">
            {mainNav.map(item => <NavLink key={item.to} to={item.to} className={({
            isActive
          }) => `group relative whitespace-nowrap py-2 text-[14px] transition-colors duration-150 ease-soft hover:text-ink ${isActive ? 'text-ink' : 'text-muted'}`} data-id="element-8064" data-mp="c2y5t9ufipow">
                {({
              isActive
            }) => <>
                    {item.label}
                    <span aria-hidden="true" className={`absolute bottom-0.5 left-0 h-px w-full origin-left bg-clay transition-transform duration-200 ease-soft ${isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} data-id="element-8065" data-mp="8z8v7l665iov" />
                  </>}
              </NavLink>)}
          </nav>

          <div className="hidden items-center gap-1 lg:flex" data-id="element-8066" data-mp="15qszh8kde66w">
            <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={btnGhost} data-id="element-8067" data-mp="15r2pgw5f0dcl">
              <SparklesIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8068" data-mp="1kekvpyymlg14" />
              {/* Op smallere schermen alleen het icoon; de naam blijft voor schermlezers. */}
              <span className="sr-only xl:not-sr-only" data-id="element-8069" data-mp="1bdmo76ozaqds">{pageActions.quiz}</span>
            </button>
            <Link to="/account" aria-label={accountLabel} title={accountLabel} className={iconBtn} data-id="element-8070" data-mp="1gb5118649pr2">
              <UserIcon className="h-[18px] w-[18px]" strokeWidth={1.5} aria-hidden="true" data-id="element-8071" data-mp="11ms68eirmcy5" />
              {signedIn && <span aria-hidden="true" className="absolute right-2 top-2 h-2 w-2 rounded-full bg-clay ring-2 ring-shell" data-id="element-8072" data-mp="16es7qbehhdwu" />}
            </Link>
            <CartButton onClick={openCart} data-id="element-8073" data-mp="sk94xyumo8e0" />
            <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={`ml-2 ${btnPrimary}`} data-id="element-8074" data-mp="wn5rg0nagon8">
              {pageActions.book}
            </button>
          </div>

          <div className="-mr-2 flex items-center gap-1 lg:hidden" data-id="element-8075" data-mp="np3gotdr6hmr">
            <CartButton onClick={openCart} data-id="element-8076" data-mp="134qplvqq0324" />
            <button ref={toggleRef} type="button" aria-label={shopLabels.menu(open)} aria-expanded={open} aria-controls={menuId} onClick={() => setOpen(v => !v)} className={iconBtn} data-id="element-8077" data-mp="y6fe7gks1khs">
              {open ? <XIcon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" data-id="element-8078" data-mp="k64paj312fz9" /> : <MenuIcon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" data-id="element-8079" data-mp="k8hf1if11umv" />}
            </button>
          </div>
        </div>

        <AnimatePresence initial={false} data-id="element-8080" data-mp="ag3a6gsnk7wh">
          {open && <MobileMenu id={menuId} accountLabel={accountLabel} onQuiz={runAndClose(openQuiz)} onBooking={runAndClose(() => openBooking())} data-id="element-8081" data-mp="aeih6vlzkx0q" />}
        </AnimatePresence>
      </header>
    </div>;
}